import { debounce } from './utils.js';

if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    #next; #prev; #slides; #container; #resizeObserver;
    activeIndex = 0;

    constructor() {
      super();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.#init());
    }

    #init() {
      this.#next = this.querySelector('.next-button') || null;
      this.#prev = this.querySelector('.prev-button') || null;
      this.#container = this.querySelector('.simple-slider-items') || null;
      this.#slides = this.querySelectorAll('.simple-slider-item');

      // must have a container + at least one slide
      if (!this.#container || this.#slides.length === 0) return;

      // Wire buttons only if they exist
      this.#next?.addEventListener('click', this.scrollToRight);
      this.#prev?.addEventListener('click', this.scrollToLeft);

      // Initial state (no-op if no buttons)
      requestAnimationFrame(() => this.handleDisableButtons());

      // Scroll / resize observers
      this.#container.addEventListener(
        'scroll',
        debounce(this.handleDisableButtons, 100)
      );
      this.observeResizeContainer(debounce(this.handleDisableButtons, 100));

      // Enable free drag
      this.enableDragScroll();

      // remove preinit for CSS transitions, etc.
      setTimeout(() => this.removeAttribute('preinit'), 100);

      // scroll active button into view
      this.scrollActiveIntoView();
    }

    enableDragScroll() {
      const host = this;                // <simple-slider>
      const scroller = this.#container; // .simple-slider-items
      const DRAG_ACTIVATE_PX = 4;
      const CLICK_SUPPRESS_PX = 6;

      let maybeDrag = false;
      let dragging = false;
      let startX = 0;
      let startScrollLeft = 0;
      let dragDistAbs = 0;
      let activePointerId = null;
      let suppressClick = false;

      const isOnArrow = (t) => t.closest?.('.prev-button, .next-button') != null;

      host.addEventListener('dragstart', (e) => e.preventDefault(), { capture: true });
      scroller.querySelectorAll('a, img').forEach(n => { n.draggable = false; });

      const onPointerDown = (e) => {
        // left mouse / touch / pen only
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        if (isOnArrow(e.target)) {
          maybeDrag = false;
          dragging = false;
          return;
        }

        maybeDrag = true;
        dragging = false;
        activePointerId = e.pointerId;
        startX = e.clientX;
        startScrollLeft = scroller.scrollLeft;
        dragDistAbs = 0;
        suppressClick = false;
      };

      const onPointerMove = (e) => {
        if (e.pointerId !== activePointerId) return;

        const dx = e.clientX - startX;
        dragDistAbs = Math.max(dragDistAbs, Math.abs(dx));

        if (!dragging && maybeDrag && Math.abs(dx) >= DRAG_ACTIVATE_PX) {
          dragging = true;
          maybeDrag = false;

          if (e.cancelable) e.preventDefault();

          try { host.setPointerCapture(activePointerId); } catch {}

          host.classList.add('is-dragging');
          scroller.classList.add('is-dragging');
        }

        if (dragging) {
          if (e.cancelable) e.preventDefault();
          scroller.scrollLeft = startScrollLeft - dx;
        }
      };

      const endDrag = () => {
        if (dragging) {
          try { host.releasePointerCapture(activePointerId); } catch {}
        }
        host.classList.remove('is-dragging');
        scroller.classList.remove('is-dragging');

        if (dragDistAbs > CLICK_SUPPRESS_PX) {
          suppressClick = true;
          setTimeout(() => { suppressClick = false; }, 0);
        }

        maybeDrag = false;
        dragging = false;
        activePointerId = null;

        this.handleDisableButtons();
      };

      host.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
      host.addEventListener('pointermove',  onPointerMove,  { capture: true, passive: false });
      host.addEventListener('pointerup',    endDrag,        { capture: true });
      host.addEventListener('pointercancel',endDrag,        { capture: true });

      // Arrow clicks
      host.addEventListener('click', (e) => {
        if (isOnArrow(e.target)) return;
        if (suppressClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    scrollActiveIntoView() {
      const active = this.querySelector('.quicklink.is-active');
      const container = this.querySelector('.simple-slider-items');

      if (!active || !container) return;

      requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();

        const offsetLeft =
          activeRect.left -
          containerRect.left +
          container.scrollLeft;

       const target =
        offsetLeft -
        (container.clientWidth * 0.35);

        container.scrollTo({
          left: Math.max(0, target),
          behavior: 'instant',
        });

        this.handleDisableButtons();
      });
    }

    disconnectedCallback() {
      if (this.#container) {
        this.#container.removeEventListener('scroll', this.handleDisableButtons);
      }
      if (this.#resizeObserver && this.#container) {
        this.#resizeObserver.unobserve(this.#container);
      }
    }

    getMaxScroll() {
      if (!this.#container) return 0;

      return Math.max(0, this.#container.scrollWidth - this.#container.clientWidth);
    }

    scrollToPosition(left) {
      if (!this.#container) return;

      const maxScroll = this.getMaxScroll();

      this.#container.scrollTo({
        left: Math.max(0, Math.min(left, maxScroll)),
        behavior: 'smooth',
      });
    }

    // Arrow functions
    scrollToRight = () => {
      if (!this.#container) return;

      const current = this.#container.scrollLeft;
      const slides = Array.from(this.#slides);
      const tolerance = 2;

      const nextSlide = slides.find((slide) => {
        return slide.offsetLeft > current + tolerance;
      });

      if (!nextSlide) {
        this.scrollToPosition(this.getMaxScroll());
        return;
      }

      this.scrollToPosition(nextSlide.offsetLeft);
    };

    scrollToLeft = () => {
      if (!this.#container) return;

      const current = this.#container.scrollLeft;
      const slides = Array.from(this.#slides);
      const tolerance = 2;

      const previousSlide = slides
        .slice()
        .reverse()
        .find((slide) => {
          return slide.offsetLeft < current - tolerance;
        });

      if (!previousSlide) {
        this.scrollToPosition(0);
        return;
      }

      this.scrollToPosition(previousSlide.offsetLeft);
    };

    isScrolledToEdge() {
      const el = this.#container;
      const gap = Math.floor(el.clientWidth * 0.005);
      return {
        left: el.scrollLeft <= gap,
        right: el.scrollLeft + el.clientWidth >= el.scrollWidth - gap,
      };
    }

    hasScrollableContent() {
      if (!this.#container) return false;

      return this.#container.scrollWidth > this.#container.clientWidth + 2;
    }

    handleDisableButtons = () => {
      if (!this.#container) return;

      const hasScroll = this.hasScrollableContent();

      this.toggleAttribute('has-scroll', hasScroll);

      if (!this.#next && !this.#prev) return;

      if (!hasScroll) {
        this.#prev?.setAttribute('disabled', '');
        this.#next?.setAttribute('disabled', '');
        return;
      }

      const { left, right } = this.isScrolledToEdge();

      this.#prev?.toggleAttribute('disabled', left);
      this.#next?.toggleAttribute('disabled', right);
    };

    observeResizeContainer(cb) {
      if (!this.#container) return;
      this.#resizeObserver = new ResizeObserver(() => cb());
      this.#resizeObserver.observe(this.#container);
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}